
import { defineComponent, onMounted, ref, computed } from "vue";
import { store } from "@/store";
import { canvasWrap } from "@/canvas-api/Canvas";
import {
  setViewPortValue,
  scrollBarHorizontal,
  scrollBarVertical,
  scrollerHorizontal,
  scrollerVertical,
} from "@/canvas-api/ViewPort";
import { mobileZoom } from "@/canvas-api/Zoom";

export default defineComponent({
  name: "ScrollBar",
  setup() {
    const isMobile = computed(() => store.getters.isMobile);
    const currentActiveObject = computed(() => store.getters.currentActiveObject);

    const isDragging = ref(false);
    const isDraggingX = ref(false);
    const isDraggingY = ref(false);

    const mouseX = ref(0);
    const mouseY = ref(0);

    const touchDiffStart = ref(0);

    const handleMouseDownScrollX = (ev: MouseEvent) => {
      ev.stopPropagation();

      isDragging.value = true;
      isDraggingX.value = true;
      mouseX.value = ev.clientX;
    };

    const handleMouseDownScrollY = (ev: MouseEvent) => {
      ev.stopPropagation();

      isDragging.value = true;
      isDraggingY.value = true;
      mouseY.value = ev.clientY;
    };

    const handleMouseDownCanvasWrap = (ev: MouseEvent) => {
      ev.stopPropagation();

      if (currentActiveObject.value) return;

      if (ev.shiftKey === true) {
        isDragging.value = true;
        isDraggingX.value = true;
        isDraggingY.value = true;
        mouseX.value = ev.clientX;
        mouseY.value = ev.clientY;
      }
    };

    const handleMouseUpScroll = () => {
      isDragging.value = false;
      isDraggingX.value = false;
      isDraggingY.value = false;
    };

    const handleMouseMoveScroll = (ev: MouseEvent) => {
      if (isDragging.value) {
        ev.stopPropagation();
        ev.preventDefault();

        if (isDraggingX.value) {
          if (scrollBarHorizontal.value && scrollerHorizontal.value) {
            const diffX = ev.clientX - mouseX.value;

            const scrollBarOffsetWidth = scrollBarHorizontal.value.offsetWidth;
            const scrollerOffsetLeft = scrollerHorizontal.value.offsetLeft;
            const scrollerWidth = scrollerHorizontal.value.offsetWidth;

            handleScroller(
              scrollerHorizontal.value,
              scrollBarOffsetWidth,
              scrollerOffsetLeft,
              scrollerWidth,
              diffX,
              "left",
              "x"
            );

            mouseX.value = ev.clientX;
          }
        }

        if (isDraggingY.value) {
          if (scrollBarVertical.value && scrollerVertical.value) {
            const diffY = ev.clientY - mouseY.value;

            const scrollBarOffsetHeight = scrollBarVertical.value.offsetHeight;
            const scrollerOffsetTop = scrollerVertical.value.offsetTop;
            const scrollerHeight = scrollerVertical.value.offsetHeight;

            handleScroller(
              scrollerVertical.value,
              scrollBarOffsetHeight,
              scrollerOffsetTop,
              scrollerHeight,
              diffY,
              "top",
              "y"
            );

            mouseY.value = ev.clientY;
          }
        }
      }
    };

    const handleTouchStartScroll = (ev: TouchEvent) => {
      isDragging.value = true;

      const touches = ev.touches;

      mouseX.value = touches[0].clientX;
      mouseY.value = touches[0].clientY;

      if (touches.length > 1) {
        touchDiffStart.value = Math.hypot(
          ev.touches[0].pageX - ev.touches[1].pageX,
          ev.touches[0].pageY - ev.touches[1].pageY
        );
      }
    };

    const handleTouchEndScroll = (ev: TouchEvent) => {
      isDragging.value = false;
    };

    const handleTouchCancelScroll = (ev: TouchEvent) => {
      isDragging.value = false;
    };

    const handleTouchMoveScroll = (ev: TouchEvent) => {
      if (currentActiveObject.value) return;

      const touches = ev.touches;

      if (isDragging.value && touches.length) {
        ev.preventDefault();

        if (touches.length > 1) {
          const curTouchDiff = Math.hypot(
            ev.touches[0].pageX - ev.touches[1].pageX,
            ev.touches[0].pageY - ev.touches[1].pageY
          );

          if (touchDiffStart.value > curTouchDiff) {
            mobileZoom(-1);
          }

          if (touchDiffStart.value < curTouchDiff) {
            mobileZoom(1);
          }
        } else {
          if (scrollBarHorizontal.value && scrollerHorizontal.value) {
            const diffX = touches[0].clientX - mouseX.value;

            const scrollBarOffsetWidth = scrollBarHorizontal.value.offsetWidth;
            const scrollerOffsetLeft = scrollerHorizontal.value.offsetLeft;
            const scrollerWidth = scrollerHorizontal.value.offsetWidth;

            handleScroller(
              scrollerHorizontal.value,
              scrollBarOffsetWidth,
              scrollerOffsetLeft,
              scrollerWidth,
              -diffX,
              "left",
              "x"
            );

            mouseX.value = touches[0].clientX;
          }

          if (scrollBarVertical.value && scrollerVertical.value) {
            const diffY = touches[0].clientY - mouseY.value;

            const scrollBarOffsetHeight = scrollBarVertical.value.offsetHeight;
            const scrollerOffsetTop = scrollerVertical.value.offsetTop;
            const scrollerHeight = scrollerVertical.value.offsetHeight;

            handleScroller(
              scrollerVertical.value,
              scrollBarOffsetHeight,
              scrollerOffsetTop,
              scrollerHeight,
              -diffY,
              "top",
              "y"
            );

            mouseY.value = touches[0].clientY;
          }
        }
      }
    };

    const handleScrollEvent = (ev: WheelEvent) => {
      ev.stopPropagation();
      ev.preventDefault();

      if (ev.shiftKey) {
        if (scrollBarHorizontal.value && scrollerHorizontal.value) {
          const diffX = ev.deltaY;

          const scrollBarOffsetWidth = scrollBarHorizontal.value.offsetWidth;
          const scrollerOffsetLeft = scrollerHorizontal.value.offsetLeft;
          const scrollerWidth = scrollerHorizontal.value.offsetWidth;

          handleScroller(
            scrollerHorizontal.value,
            scrollBarOffsetWidth,
            scrollerOffsetLeft,
            scrollerWidth,
            diffX,
            "left",
            "x"
          );
        }
      } else {
        if (scrollBarHorizontal.value && scrollerHorizontal.value) {
          const diffX = ev.deltaX;

          const scrollBarOffsetWidth = scrollBarHorizontal.value.offsetWidth;
          const scrollerOffsetLeft = scrollerHorizontal.value.offsetLeft;
          const scrollerWidth = scrollerHorizontal.value.offsetWidth;

          handleScroller(
            scrollerHorizontal.value,
            scrollBarOffsetWidth,
            scrollerOffsetLeft,
            scrollerWidth,
            diffX,
            "left",
            "x"
          );
        }

        if (scrollBarVertical.value && scrollerVertical.value) {
          const diffY = ev.deltaY;

          const scrollBarOffsetHeight = scrollBarVertical.value.offsetHeight;
          const scrollerOffsetTop = scrollerVertical.value.offsetTop;
          const scrollerHeight = scrollerVertical.value.offsetHeight;

          handleScroller(
            scrollerVertical.value,
            scrollBarOffsetHeight,
            scrollerOffsetTop,
            scrollerHeight,
            diffY,
            "top",
            "y"
          );
        }
      }
    };

    const handleScroller = (
      scroller: HTMLDivElement,
      scrollBarOffsetDimension: number, //scrollBar offsetWidth or offsetHeight
      scrollerOffsetSide: number, //scroller offsetTop or offsetLeft
      scrollerDimension: number, //scroller width or height
      diff: number,
      side: string,
      axis: string
    ) => {
      if (scrollerOffsetSide + diff < 0) {
        if (scrollerOffsetSide > 0) {
          setViewPortValue(-scrollerOffsetSide, axis);
          setScroller(scroller, side, 0);
        }
      } else if (scrollerOffsetSide + scrollerDimension + diff > scrollBarOffsetDimension) {
        const remaining = scrollBarOffsetDimension - scrollerDimension - scrollerOffsetSide;

        if (remaining > 0) {
          setViewPortValue(remaining, axis);
          setScroller(scroller, side, scrollBarOffsetDimension - scrollerDimension);
        }
      } else {
        setViewPortValue(diff, axis);
        setScroller(scroller, side, scrollerOffsetSide + diff);
      }
    };

    const setScroller = (scroller: HTMLDivElement, side: string, value: number) => {
      if (side === "top") {
        scroller.style.top = `${value}px`;
      } else {
        scroller.style.left = `${value}px`;
      }
    };

    onMounted(async () => {
      if (canvasWrap.value) {
        if (isMobile.value) {
          canvasWrap.value.addEventListener("touchstart", handleTouchStartScroll, false);
          canvasWrap.value.addEventListener("touchend", handleTouchEndScroll, false);
          canvasWrap.value.addEventListener("touchcancel", handleTouchCancelScroll, false);
          canvasWrap.value.addEventListener("touchmove", handleTouchMoveScroll, false);
        } else {
          canvasWrap.value.addEventListener("wheel", handleScrollEvent);
          canvasWrap.value.addEventListener("mousedown", handleMouseDownCanvasWrap);
          window.addEventListener("mouseup", handleMouseUpScroll);
          window.addEventListener("mousemove", handleMouseMoveScroll);
        }
      }
    });

    return {
      scrollBarHorizontal,
      scrollBarVertical,
      scrollerHorizontal,
      scrollerVertical,
      handleMouseDownScrollX,
      handleMouseDownScrollY,
      handleMouseUpScroll,
    };
  },
});
